import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { identity } from 'ramda'
import {
  Flex,
  FlexCol,
  HoverableInfoIcon,
  RadioButtonGroup,
} from 'components/patterns'
import { TimerInput } from 'components/patterns/Inputs'
import {
  signalTranslation,
  featureSignalTranslationValueLabel,
  featureSignalTranslationValueTooltip,
} from 'i18n/displayConstants/signals'
import strings from 'utils/strings'
import featuresStrings from 'utils/strings/features-strings'
import { EVAL_VALUES } from 'components/utils'
import {
  TRANSLATION_TYPES,
  BOOLEAN,
  STATE,
  CHEC_MAX_VALUE,
} from 'utils/constants'
import { SIGNAL_TRANSLATION_BOOLEAN_TYPES } from 'constants/signals'
import { isArray } from 'utils/types'
import { TIMER_MASK_HMMSSS } from 'constants/ui/timers'
import { stylesIf } from 'utils/styles'

import conditionSchema from 'schemas/validation/feature/conditionSchema'
import { schemaProp, yup } from 'schemas/validation'

import {
  StyledSelectMenu,
  StyledMenuItem,
  StyledValue,
  StyledNumberInput,
  CustomTooltipGlobalStyles,
} from './InputValueEditor.style.js'

const rule = schemaProp(conditionSchema)

const translationButtons = (translations, disableFalseValue, currentValue) => {
  if (translations && isArray(translations)) {
    const disabled = (value) =>
      (disableFalseValue && value === EVAL_VALUES.FALSE) ||
      (disableFalseValue && value !== currentValue)

    return translations.map(({ value, translation }) => ({
      value,
      text: translation ? signalTranslation(translation) : value,
      disabled: disabled(value),
    }))
  }

  return null
}

const translationSelect = (translations, disableFalseValue, currentValue) =>
  translations.map(({ value, translation }) => ({
    key: value,
    value,
    disabled: disableFalseValue && value !== currentValue,
    title: signalTranslation(translation),
  }))

const InputValueEditor = ({
  value,
  isTimer,
  onInputValueChange,
  translationType,
  translations,
  maxValue,
  minValue,
  units,
  inputConfig,
  customRule,
  name,
  makeLabel,
  disableFalseValue,
  disabled,
  noLabel,
  narrow,
  definedSnaValues = [],
  definedErrorValues = [],
}) => {
  const hasTranslations = TRANSLATION_TYPES.has(translationType)
  const labelIf = stylesIf(!noLabel)

  const renderInputTimer = () => (
    <TimerInput
      vertical
      label={labelIf(strings.input_value)}
      hint={strings.carry_timer_initial_value_hint}
      name={strings.timer}
      value={value}
      onChange={onInputValueChange}
      rules={customRule}
      mask={TIMER_MASK_HMMSSS}
      className="form-field"
      narrow={narrow}
    />
  )

  const renderNumberInput = () => {
    const numberLabel = makeLabel(
      featureSignalTranslationValueLabel(units) || strings.input_value
    )

    const rules = customRule || rule('value', { minValue, maxValue })

    return (
      <StyledNumberInput
        tooltip={featureSignalTranslationValueTooltip(inputConfig)}
        hint={`${minValue}-${maxValue}`}
        label={labelIf(numberLabel)}
        name={name}
        value={value}
        onChange={onInputValueChange}
        className="form-field"
        rules={rules}
        narrow={narrow}
        disabled={disabled}
        data-testid="input-value-input"
        wrapperDataTestId="input-value-wrapper"
      />
    )
  }

  const renderTranslationRadios = () => {
    const snaButtons = definedSnaValues.map((v) => ({
      value: v,
      text: signalTranslation(SIGNAL_TRANSLATION_BOOLEAN_TYPES.NOT_AVAILABLE),
      disabled: false,
    }))
    const buttons = [
      ...(translationButtons(translations, disableFalseValue, value) || []),
      ...snaButtons,
    ]
    return (
      <RadioButtonGroup
        vertical
        label={labelIf(strings.value)}
        onChange={(e) => onInputValueChange(e.target.value)}
        currentValue={value}
        buttons={buttons}
        radioGroupClassName="input-value-radio-group"
      />
    )
  }

  const renderTranslationSelect = () => (
    <StyledSelectMenu
      vertical
      className="form-field"
      label={labelIf(makeLabel(strings.value))}
      options={translationSelect(translations, disableFalseValue, value)}
      onChange={onInputValueChange}
      value={value}
      mask={formatSelect}
      name={name}
      hasSelectedSection
      rules={customRule || rule('value')}
      narrow={narrow}
      data-testid="input-value-select"
    />
  )

  const renderStrategy = () => {
    if (isTimer) return renderInputTimer()

    if (hasTranslations) {
      return {
        [BOOLEAN]: renderTranslationRadios(),
        [STATE]: renderTranslationSelect(),
      }[translationType]
    }

    if (definedSnaValues.length > 0 || definedErrorValues.length > 0) {
      return (
        <ValueOrRangeSelect
          value={value}
          definedSnaValues={definedSnaValues}
          definedErrorValues={definedErrorValues}
          onInputValueChange={onInputValueChange}
          name={name}
          makeLabel={makeLabel}
          narrow={narrow}
          disableFalseValue={disableFalseValue}
          inputConfig={inputConfig}
          units={units}
          label={labelIf(strings.input_value)}
          renderNumberInput={renderNumberInput}
        />
      )
    }

    return renderNumberInput()
  }

  return renderStrategy()
}

InputValueEditor.defaultProps = {
  isTimer: false,
  disabled: false,
  onInputValueChange: () => {},
  translations: [],
  minValue: 0,
  maxValue: CHEC_MAX_VALUE,
  name: strings.input_value,
  makeLabel: identity,
}

InputValueEditor.propTypes = {
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onInputValueChange: PropTypes.func,
  isTimer: PropTypes.bool,
  disabled: PropTypes.bool,
  disableFalseValue: PropTypes.bool,
  maxValue: PropTypes.number,
  minValue: PropTypes.number,
  units: PropTypes.string,
  translations: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.number,
      translation: PropTypes.string,
    })
  ),
  dataType: PropTypes.string,
  noLabel: PropTypes.bool,
  narrow: PropTypes.bool,
}

export default InputValueEditor

const formatSelect = (text, value) => (
  <StyledMenuItem>
    <span>{text}</span>
    <StyledValue size="small">{value}</StyledValue>
  </StyledMenuItem>
)

const SettingsHelp = () => (
  <div>
    {featuresStrings.select_signal}
    <ul>
      <li>{featuresStrings.signal_type_input.value}</li>
      <li>{featuresStrings.signal_type_input.error}</li>
      <li>{featuresStrings.signal_type_input.sna}</li>
    </ul>
  </div>
)

export const ValueOrRangeSelect = (props) => {
  const {
    value,
    definedErrorValues = [],
    definedSnaValues = [],
    onInputValueChange,
    name,
    label,
    renderNumberInput,
  } = props

  const isSnaSelected = definedSnaValues.includes(value)
  const isErrorSelected = definedErrorValues.includes(value)
  const isValueSelected = !isSnaSelected && !isErrorSelected
  const [selectedKey, setSelectedKey] = useState(
    isValueSelected ? 'value' : isSnaSelected ? 'sna' : 'error'
  )
  const handleSelectChange = (key) => {
    setSelectedKey(key)
    if (key === 'value') {
      onInputValueChange(0)
    }
    if (key === 'sna' && !definedSnaValues.includes(value)) {
      const [firstValue] = definedSnaValues
      if (firstValue) onInputValueChange(firstValue)
    }
    if (key === 'error' && !definedErrorValues.includes(value)) {
      const [firstValue] = definedErrorValues
      if (firstValue) onInputValueChange(firstValue)
    }
  }

  return (
    <div style={{ width: '100%' }}>
      <Flex alignItems="flex-start">
        <FlexCol span="8">
          <StyledSelectMenu
            vertical
            className="form-field"
            data-testid="setting-type-select"
            label={
              <>
                <CustomTooltipGlobalStyles />
                {strings.setting_type}{' '}
                <HoverableInfoIcon
                  tooltip={SettingsHelp}
                  tooltipPosition="top"
                  // applies only to this tooltip
                  overlayClassName="custom-tooltip"
                />
              </>
            }
            onChange={handleSelectChange}
            mask={formatSelect}
            value={selectedKey}
            name={name}
            rules={yup.string()}
            width={'50%'}
            options={[
              {
                key: 'value',
                value: 'value',
                disabled: false,
                title: strings.value,
              },

              ...(definedSnaValues.length > 0
                ? [
                    {
                      key: 'sna',
                      value: 'sna',
                      disabled: false,
                      title: strings.signal_sna,
                    },
                  ]
                : []),

              ...(definedErrorValues.length > 0
                ? [
                    {
                      key: 'error',
                      value: 'error',
                      disabled: false,
                      title: strings.error,
                    },
                  ]
                : []),
            ]}
          />
        </FlexCol>
        <FlexCol span="8" push="1">
          {selectedKey === 'value' && renderNumberInput()}

          {selectedKey === 'sna' && (
            <StyledSelectMenu
              vertical
              className="form-field"
              label={label}
              options={definedSnaValues.map((v) => ({
                key: v,
                value: v,
                disabled: false,
                title: v,
              }))}
              onChange={onInputValueChange}
              value={value}
              mask={formatSelect}
              name={name}
              rules={yup.number().oneOf(definedSnaValues)}
              disabled={definedErrorValues.length === 1}
              width="50%"
            />
          )}

          {selectedKey === 'error' && (
            <StyledSelectMenu
              vertical
              className="form-field"
              label={label}
              options={definedErrorValues.map((v) => ({
                key: v,
                value: v,
                disabled: false,
                title: v,
              }))}
              onChange={onInputValueChange}
              value={value}
              mask={formatSelect}
              name={name}
              rules={yup.number().oneOf(definedErrorValues)}
              disabled={definedErrorValues.length === 1}
              width="50%"
            />
          )}
        </FlexCol>
      </Flex>
    </div>
  )
}

ValueOrRangeSelect.propTypes = {
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  definedErrorValues: PropTypes.arrayOf(PropTypes.number),
  definedSnaValues: PropTypes.arrayOf(PropTypes.number),
  onInputValueChange: PropTypes.func.isRequired,
  name: PropTypes.string,
  makeLabel: PropTypes.func,
  narrow: PropTypes.bool,
  label: PropTypes.string,
  renderNumberInput: PropTypes.func.isRequired,
  rules: PropTypes.object,
}
